import React, { useState, useCallback } from 'react';
import {
  Button, Select, Input, Space, Card, Divider, Checkbox, Collapse,
  Form, Tooltip, Tag,
} from 'antd';
import {
  PlusOutlined, DeleteOutlined, DownOutlined, RightOutlined,
  RobotOutlined, TableOutlined,
} from '@ant-design/icons';
import AiStaffSelectField from './AiStaffSelectField';
import WorkflowKnowledgeBaseSettingsControl from './WorkflowKnowledgeBaseSettingsControl';

const { Option } = Select;
const { Panel } = Collapse;

const MODES = ['copyPv', 'fixedValue', 'jsonToPv', 'pvToJson', 'eFormToPv', 'ai'];

const AI_SOURCE_OPTIONS = [
  { value: 'integrateWaitReply',        labelKey: 'sendEForm.integrateWaitReply' },
  { value: 'integrateChatBot',          labelKey: 'sendEForm.integrateChatBot' },
  { value: 'integrateChatOrderingCart', labelKey: 'sendEForm.integrateChatOrderingCart' },
  { value: 'integrateEForm',            labelKey: 'sendEForm.integrateEForm' },
  { value: 'integrateEmailStart',       labelKey: 'sendEForm.integrateEmailStart' },
  { value: 'integrateManualStart',      labelKey: 'sendEForm.integrateManualStart' },
];

const defaultRule = () => ({
  _id: Math.random().toString(36).slice(2),
  mode: 'copyPv',
  sourceVariable: '',
  sourceJsonKey: '',
  targetVariable: '',
  targetJsonKey: '',
  fixedValue: '',
  jsonFieldMappings: [],
  // eFormToPv
  eFormNodeId: '',
  eFormFieldName: '',
  multiInstanceMode: 'first',
  joinSeparator: ', ',
  // ai
  generationModes: ['integrateWaitReply'],
  integratedWaitReplyNodeId: '',
  integratedChatBotNodeId: '',
  integratedChatOrderingCartNodeId: '',
  integratedEFormNodeId: '',
  integratedDataSetQueryNodeId: '',
  aiProviderKey: '',
  aiStaffId: null,
  additionalPrompt: '',
  outputJsonKey: '',
  knowledgeBaseEnabled: false,
  knowledgeBaseDataSetId: '',
  knowledgeBaseQueryConditionGroups: [],
  knowledgeStudioDocumentIds: [],
});

const defaultJsonMapping = () => ({
  _id: Math.random().toString(36).slice(2),
  jsonKey: '',
  valueSource: 'pv',
  sourceVariable: '',
  sourceJsonKey: '',
  fixedValue: '',
});

/**
 * 流程資料格節點屬性編輯元件。
 * Props:
 *   assignmentRules  – 規則陣列（從 selectedNode.data.assignmentRules）
 *   onChange         – (newRules) => void
 *   processVariables – 流程資料列表
 *   nodes            – 畫布所有節點（供 AI 來源節點選擇）
 *   selectedNode     – 當前節點（供 AiStaffSelectField 使用）
 *   aiProviders      – AI Provider 清單
 *   aiStaffs         – AI 員工清單
 *   aiStaffOptions   – AI 員工選項
 *   loadingAiStaffs  – boolean
 *   handleAiStaffChange – fn
 *   handleNodeDataChange – fn（供 AiStaffSelectField 使用）
 *   t                – i18n translate function
 */
const ProcessDataCellEditor = ({
  assignmentRules = [],
  onChange,
  processVariables = [],
  nodes = [],
  eformFieldsMap = {},
  selectedNode,
  aiProviders = [],
  aiStaffs = [],
  aiStaffOptions = [],
  loadingAiStaffs = false,
  handleAiStaffChange,
  handleNodeDataChange,
  t,
}) => {
  const [expandedRules, setExpandedRules] = useState({});

  const rules = assignmentRules.map((r) =>
    r._id ? r : { ...r, _id: Math.random().toString(36).slice(2) }
  );

  const update = useCallback(
    (newRules) => onChange(newRules.map(({ _id, ...rest }) => ({ _id, ...rest }))),
    [onChange]
  );

  const addRule = () => update([...rules, defaultRule()]);

  const removeRule = (idx) => {
    const next = rules.filter((_, i) => i !== idx);
    update(next);
  };

  const patchRule = (idx, patch) => {
    const next = rules.map((r, i) => (i === idx ? { ...r, ...patch } : r));
    update(next);
  };

  const toggleExpand = (id) =>
    setExpandedRules((prev) => ({ ...prev, [id]: !prev[id] }));

  const modeLabel = (mode) => {
    const map = {
      copyPv: t('workflowDesigner.processDataCell.modeCopyPv'),
      fixedValue: t('workflowDesigner.processDataCell.modeFixedValue'),
      jsonToPv: t('workflowDesigner.processDataCell.modeJsonToPv'),
      pvToJson: t('workflowDesigner.processDataCell.modePvToJson'),
      eFormToPv: t('workflowDesigner.processDataCell.modeEFormToPv'),
      ai: t('workflowDesigner.processDataCell.modeAi'),
    };
    return map[mode] || mode;
  };

  // 僅 json 型流程資料
  const jsonPvs = processVariables.filter((pv) => pv.dataType === 'json');

  // waitReply / chatBot / chatOrderingCart / eForm 節點列表（供 AI 來源選擇器）
  const waitReplyNodes          = nodes.filter((n) => n.data?.type === 'waitReply');
  const chatBotNodes            = nodes.filter((n) => n.data?.type === 'chatBot');
  const chatOrderingCartNodes   = nodes.filter((n) => n.data?.type === 'chatOrderingCart');
  const eFormNodes              = nodes.filter((n) => n.data?.type === 'sendEForm');

  const renderRule = (rule, idx) => {
    const expanded = !!expandedRules[rule._id];
    const isAi = rule.mode === 'ai';
    const isPvToJson = rule.mode === 'pvToJson';
    const isEFormToPv = rule.mode === 'eFormToPv';

    return (
      <Card
        key={rule._id}
        size="small"
        style={{ marginBottom: 8, borderColor: isAi ? '#1677ff' : undefined }}
        title={
          <Space>
            <Tag color={isAi ? 'blue' : 'default'} style={{ marginRight: 0 }}>
              {modeLabel(rule.mode)}
            </Tag>
            <span style={{ fontSize: 12, color: '#888' }}>#{idx + 1}</span>
          </Space>
        }
        extra={
          <Space>
            <Button
              type="text"
              size="small"
              icon={expanded ? <DownOutlined /> : <RightOutlined />}
              onClick={() => toggleExpand(rule._id)}
            />
            <Button
              type="text"
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => removeRule(idx)}
            />
          </Space>
        }
      >
        {/* 操作類型 */}
        <Form.Item
          label={t('workflowDesigner.processDataCell.mode')}
          style={{ marginBottom: 8 }}
        >
          <Select
            value={rule.mode}
            onChange={(v) => patchRule(idx, { mode: v })}
            size="small"
          >
            {MODES.map((m) => (
              <Option key={m} value={m}>
                {modeLabel(m)}
              </Option>
            ))}
          </Select>
        </Form.Item>

        {/* copyPv */}
        {rule.mode === 'copyPv' && (
          <RuleRowCopyPv
            rule={rule}
            idx={idx}
            processVariables={processVariables}
            patchRule={patchRule}
            t={t}
          />
        )}

        {/* fixedValue */}
        {rule.mode === 'fixedValue' && (
          <RuleRowFixedValue
            rule={rule}
            idx={idx}
            processVariables={processVariables}
            jsonPvs={jsonPvs}
            patchRule={patchRule}
            t={t}
          />
        )}

        {/* jsonToPv */}
        {rule.mode === 'jsonToPv' && (
          <RuleRowJsonToPv
            rule={rule}
            idx={idx}
            processVariables={processVariables}
            jsonPvs={jsonPvs}
            patchRule={patchRule}
            t={t}
          />
        )}

        {/* pvToJson */}
        {isPvToJson && (
          <RuleRowPvToJson
            rule={rule}
            idx={idx}
            processVariables={processVariables}
            jsonPvs={jsonPvs}
            patchRule={patchRule}
            t={t}
          />
        )}

        {/* eFormToPv */}
        {isEFormToPv && (
          <RuleRowEFormToPv
            rule={rule}
            idx={idx}
            processVariables={processVariables}
            jsonPvs={jsonPvs}
            eFormNodes={eFormNodes}
            eformFieldsMap={eformFieldsMap}
            patchRule={patchRule}
            t={t}
          />
        )}

        {/* ai */}
        {isAi && (
          <RuleRowAi
            rule={rule}
            idx={idx}
            processVariables={processVariables}
            waitReplyNodes={waitReplyNodes}
            chatBotNodes={chatBotNodes}
            chatOrderingCartNodes={chatOrderingCartNodes}
            eFormNodes={eFormNodes}
            aiProviders={aiProviders}
            aiStaffs={aiStaffs}
            aiStaffOptions={aiStaffOptions}
            loadingAiStaffs={loadingAiStaffs}
            handleAiStaffChange={handleAiStaffChange}
            handleNodeDataChange={handleNodeDataChange}
            selectedNode={selectedNode}
            patchRule={patchRule}
            t={t}
          />
        )}
      </Card>
    );
  };

  return (
    <div>
      <Divider orientation="left" style={{ fontSize: 13 }}>
        <TableOutlined style={{ marginRight: 6 }} />
        {t('workflowDesigner.processDataCell.title')}
      </Divider>

      {rules.length === 0 && (
        <div style={{ color: '#aaa', fontSize: 13, marginBottom: 12, textAlign: 'center' }}>
          {t('workflowDesigner.processDataCell.noRules')}
        </div>
      )}

      {rules.map((rule, idx) => renderRule(rule, idx))}

      <Button
        type="dashed"
        block
        icon={<PlusOutlined />}
        onClick={addRule}
        style={{ marginTop: 4 }}
      >
        {t('workflowDesigner.processDataCell.addRule')}
      </Button>
    </div>
  );
};

// ─── Sub-components ────────────────────────────────────────────────────────────

const PvSelect = ({ value, onChange, processVariables, placeholder, size = 'small', style }) => (
  <Select
    value={value || undefined}
    onChange={onChange}
    placeholder={placeholder}
    allowClear
    showSearch
    optionFilterProp="children"
    size={size}
    style={style}
  >
    {processVariables.map((pv) => (
      <Option key={pv.variableName} value={pv.variableName}>
        {pv.variableName}
        <span style={{ color: '#aaa', marginLeft: 6, fontSize: 11 }}>({pv.dataType})</span>
      </Option>
    ))}
  </Select>
);

const RuleRowCopyPv = ({ rule, idx, processVariables, patchRule, t }) => (
  <Space direction="vertical" style={{ width: '100%' }}>
    <Form.Item label={t('workflowDesigner.processDataCell.sourceVariable')} style={{ marginBottom: 4 }}>
      <PvSelect
        value={rule.sourceVariable}
        onChange={(v) => patchRule(idx, { sourceVariable: v })}
        processVariables={processVariables}
        placeholder={t('workflowDesigner.processDataCell.sourceVariablePlaceholder')}
      />
    </Form.Item>
    <Form.Item label={t('workflowDesigner.processDataCell.sourceJsonKey')} style={{ marginBottom: 4 }}>
      <Input
        size="small"
        value={rule.sourceJsonKey}
        onChange={(e) => patchRule(idx, { sourceJsonKey: e.target.value })}
        placeholder={t('workflowDesigner.processDataCell.sourceJsonKeyPlaceholder')}
      />
    </Form.Item>
    <Form.Item label={t('workflowDesigner.processDataCell.targetVariable')} style={{ marginBottom: 4 }}>
      <PvSelect
        value={rule.targetVariable}
        onChange={(v) => patchRule(idx, { targetVariable: v })}
        processVariables={processVariables}
        placeholder={t('workflowDesigner.processDataCell.targetVariablePlaceholder')}
      />
    </Form.Item>
  </Space>
);

const RuleRowFixedValue = ({ rule, idx, processVariables, jsonPvs, patchRule, t }) => (
  <Space direction="vertical" style={{ width: '100%' }}>
    <Form.Item label={t('workflowDesigner.processDataCell.fixedValue')} style={{ marginBottom: 4 }}>
      <Input
        size="small"
        value={rule.fixedValue}
        onChange={(e) => patchRule(idx, { fixedValue: e.target.value })}
        placeholder={t('workflowDesigner.processDataCell.fixedValuePlaceholder')}
      />
    </Form.Item>
    <Form.Item label={t('workflowDesigner.processDataCell.targetVariable')} style={{ marginBottom: 4 }}>
      <PvSelect
        value={rule.targetVariable}
        onChange={(v) => patchRule(idx, { targetVariable: v })}
        processVariables={processVariables}
        placeholder={t('workflowDesigner.processDataCell.targetVariablePlaceholder')}
      />
    </Form.Item>
    <Form.Item label={t('workflowDesigner.processDataCell.targetJsonKey')} style={{ marginBottom: 4 }}>
      <Input
        size="small"
        value={rule.targetJsonKey}
        onChange={(e) => patchRule(idx, { targetJsonKey: e.target.value })}
        placeholder={t('workflowDesigner.processDataCell.targetJsonKeyPlaceholder')}
      />
    </Form.Item>
  </Space>
);

const RuleRowJsonToPv = ({ rule, idx, processVariables, jsonPvs, patchRule, t }) => (
  <Space direction="vertical" style={{ width: '100%' }}>
    <Form.Item label={t('workflowDesigner.processDataCell.sourceVariable')} style={{ marginBottom: 4 }}>
      <PvSelect
        value={rule.sourceVariable}
        onChange={(v) => patchRule(idx, { sourceVariable: v })}
        processVariables={jsonPvs}
        placeholder={t('workflowDesigner.processDataCell.sourceVariablePlaceholder')}
      />
    </Form.Item>
    <Form.Item label={t('workflowDesigner.processDataCell.sourceJsonKey')} style={{ marginBottom: 4 }}>
      <Input
        size="small"
        value={rule.sourceJsonKey}
        onChange={(e) => patchRule(idx, { sourceJsonKey: e.target.value })}
        placeholder={t('workflowDesigner.processDataCell.sourceJsonKeyPlaceholder')}
      />
    </Form.Item>
    <Form.Item label={t('workflowDesigner.processDataCell.targetVariable')} style={{ marginBottom: 4 }}>
      <PvSelect
        value={rule.targetVariable}
        onChange={(v) => patchRule(idx, { targetVariable: v })}
        processVariables={processVariables}
        placeholder={t('workflowDesigner.processDataCell.targetVariablePlaceholder')}
      />
    </Form.Item>
  </Space>
);

const RuleRowPvToJson = ({ rule, idx, processVariables, jsonPvs, patchRule, t }) => {
  const mappings = rule.jsonFieldMappings || [];

  const patchMapping = (mIdx, patch) => {
    const next = mappings.map((m, i) => (i === mIdx ? { ...m, ...patch } : m));
    patchRule(idx, { jsonFieldMappings: next });
  };

  const addMapping = () =>
    patchRule(idx, { jsonFieldMappings: [...mappings, defaultJsonMapping()] });

  const removeMapping = (mIdx) =>
    patchRule(idx, { jsonFieldMappings: mappings.filter((_, i) => i !== mIdx) });

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <Form.Item label={t('workflowDesigner.processDataCell.targetVariable')} style={{ marginBottom: 4 }}>
        <PvSelect
          value={rule.targetVariable}
          onChange={(v) => patchRule(idx, { targetVariable: v })}
          processVariables={jsonPvs}
          placeholder={t('workflowDesigner.processDataCell.targetVariablePlaceholder')}
        />
      </Form.Item>

      <Divider orientation="left" style={{ fontSize: 12, margin: '8px 0' }}>
        {t('workflowDesigner.processDataCell.jsonFieldMappings')}
      </Divider>

      {mappings.map((m, mIdx) => (
        <Card
          key={m._id || mIdx}
          size="small"
          style={{ background: '#fafafa', marginBottom: 6 }}
          extra={
            <Button
              type="text"
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => removeMapping(mIdx)}
            />
          }
        >
          <Space direction="vertical" style={{ width: '100%' }}>
            <Form.Item label={t('workflowDesigner.processDataCell.jsonKey')} style={{ marginBottom: 4 }}>
              <Input
                size="small"
                value={m.jsonKey}
                onChange={(e) => patchMapping(mIdx, { jsonKey: e.target.value })}
                placeholder={t('workflowDesigner.processDataCell.jsonKeyPlaceholder')}
              />
            </Form.Item>
            <Form.Item label={t('workflowDesigner.processDataCell.valueSource')} style={{ marginBottom: 4 }}>
              <Select
                value={m.valueSource || 'pv'}
                onChange={(v) => patchMapping(mIdx, { valueSource: v })}
                size="small"
              >
                <Option value="pv">{t('workflowDesigner.processDataCell.valueSourcePv')}</Option>
                <Option value="fixed">{t('workflowDesigner.processDataCell.valueSourceFixed')}</Option>
              </Select>
            </Form.Item>
            {m.valueSource === 'fixed' ? (
              <Form.Item label={t('workflowDesigner.processDataCell.fixedValue')} style={{ marginBottom: 4 }}>
                <Input
                  size="small"
                  value={m.fixedValue}
                  onChange={(e) => patchMapping(mIdx, { fixedValue: e.target.value })}
                  placeholder={t('workflowDesigner.processDataCell.fixedValuePlaceholder')}
                />
              </Form.Item>
            ) : (
              <>
                <Form.Item label={t('workflowDesigner.processDataCell.sourceVariable')} style={{ marginBottom: 4 }}>
                  <PvSelect
                    value={m.sourceVariable}
                    onChange={(v) => patchMapping(mIdx, { sourceVariable: v })}
                    processVariables={processVariables}
                    placeholder={t('workflowDesigner.processDataCell.sourceVariablePlaceholder')}
                  />
                </Form.Item>
                <Form.Item label={t('workflowDesigner.processDataCell.sourceJsonKey')} style={{ marginBottom: 4 }}>
                  <Input
                    size="small"
                    value={m.sourceJsonKey}
                    onChange={(e) => patchMapping(mIdx, { sourceJsonKey: e.target.value })}
                    placeholder={t('workflowDesigner.processDataCell.sourceJsonKeyPlaceholder')}
                  />
                </Form.Item>
              </>
            )}
          </Space>
        </Card>
      ))}

      <Button
        type="dashed"
        size="small"
        icon={<PlusOutlined />}
        onClick={addMapping}
        block
      >
        {t('workflowDesigner.processDataCell.jsonFieldMappingsAdd')}
      </Button>
    </Space>
  );
};

const MULTI_INSTANCE_MODES = ['first', 'last', 'join', 'all_as_json'];

const RuleRowEFormToPv = ({ rule, idx, processVariables, jsonPvs, eFormNodes, eformFieldsMap, patchRule, t }) => {
  const multiMode = rule.multiInstanceMode || 'first';
  const showJoinSeparator = multiMode === 'join';
  const allJsonPvs = multiMode === 'all_as_json' ? jsonPvs : processVariables;

  // 取選中節點的 formId → 對應欄位清單
  const selectedEFormNode = eFormNodes.find((n) => n.id === rule.eFormNodeId);
  const formId = selectedEFormNode?.data?.formId;
  const availableFields = (formId && eformFieldsMap[formId]) || [];
  const hasFields = availableFields.length > 0;

  const multiModeLabel = (m) => {
    const map = {
      first: t('workflowDesigner.processDataCell.multiInstanceFirst'),
      last:  t('workflowDesigner.processDataCell.multiInstanceLast'),
      join:  t('workflowDesigner.processDataCell.multiInstanceJoin'),
      all_as_json: t('workflowDesigner.processDataCell.multiInstanceAllJson'),
    };
    return map[m] || m;
  };

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      {/* eForm 節點選擇 */}
      <Form.Item
        label={t('workflowDesigner.processDataCell.eFormNode')}
        style={{ marginBottom: 4 }}
      >
        <Select
          value={rule.eFormNodeId || undefined}
          onChange={(v) => patchRule(idx, { eFormNodeId: v, eFormFieldName: '' })}
          allowClear
          showSearch
          optionFilterProp="children"
          size="small"
          placeholder={t('workflowDesigner.processDataCell.eFormNodePlaceholder')}
        >
          {eFormNodes.map((n) => (
            <Option key={n.id} value={n.id}>
              {n.data?.taskName || n.id}
              {n.data?.formName ? <span style={{ color: '#aaa', marginLeft: 6, fontSize: 11 }}>({n.data.formName})</span> : null}
            </Option>
          ))}
        </Select>
      </Form.Item>

      {/* eForm 欄位名稱 — 有欄位清單則下拉，否則顯示 AutoComplete 允許手動輸入 */}
      <Form.Item
        label={t('workflowDesigner.processDataCell.eFormFieldName')}
        style={{ marginBottom: 4 }}
        extra={!rule.eFormNodeId ? null :
          !hasFields ? (
            <span style={{ color: '#faad14', fontSize: 11 }}>
              {t('workflowDesigner.processDataCell.eFormFieldsNotLoaded')}
            </span>
          ) : null
        }
      >
        {hasFields ? (
          <Select
            value={rule.eFormFieldName || undefined}
            onChange={(v) => patchRule(idx, { eFormFieldName: v })}
            allowClear
            showSearch
            optionFilterProp="label"
            size="small"
            placeholder={t('workflowDesigner.processDataCell.eFormFieldNamePlaceholder')}
          >
            {availableFields.map((f) => (
              <Option key={f.name} value={f.name} label={`${f.label || f.name} ${f.name}`}>
                <span>{f.label || f.name}</span>
                <span style={{ color: '#aaa', marginLeft: 6, fontSize: 11 }}>({f.name})</span>
              </Option>
            ))}
          </Select>
        ) : (
          <Input
            size="small"
            value={rule.eFormFieldName || ''}
            onChange={(e) => patchRule(idx, { eFormFieldName: e.target.value })}
            placeholder={t('workflowDesigner.processDataCell.eFormFieldNamePlaceholder')}
          />
        )}
      </Form.Item>

      {/* 多張表單處理方式 */}
      <Form.Item
        label={t('workflowDesigner.processDataCell.multiInstanceMode')}
        style={{ marginBottom: 4 }}
      >
        <Select
          value={multiMode}
          onChange={(v) => patchRule(idx, { multiInstanceMode: v })}
          size="small"
        >
          {MULTI_INSTANCE_MODES.map((m) => (
            <Option key={m} value={m}>{multiModeLabel(m)}</Option>
          ))}
        </Select>
      </Form.Item>

      {/* join 分隔符 */}
      {showJoinSeparator && (
        <Form.Item
          label={t('workflowDesigner.processDataCell.joinSeparator')}
          style={{ marginBottom: 4 }}
        >
          <Input
            size="small"
            value={rule.joinSeparator ?? ', '}
            onChange={(e) => patchRule(idx, { joinSeparator: e.target.value })}
            placeholder=", "
          />
        </Form.Item>
      )}

      {/* 目標流程資料 */}
      <Form.Item
        label={t('workflowDesigner.processDataCell.targetVariable')}
        style={{ marginBottom: 4 }}
      >
        <PvSelect
          value={rule.targetVariable}
          onChange={(v) => patchRule(idx, { targetVariable: v })}
          processVariables={allJsonPvs}
          placeholder={t('workflowDesigner.processDataCell.targetVariablePlaceholder')}
        />
      </Form.Item>

      {/* 目標 JSON 欄位名（可選） */}
      {multiMode !== 'all_as_json' && (
        <Form.Item
          label={t('workflowDesigner.processDataCell.targetJsonKey')}
          style={{ marginBottom: 4 }}
        >
          <Input
            size="small"
            value={rule.targetJsonKey || ''}
            onChange={(e) => patchRule(idx, { targetJsonKey: e.target.value })}
            placeholder={t('workflowDesigner.processDataCell.targetJsonKeyPlaceholder')}
          />
        </Form.Item>
      )}
    </Space>
  );
};

const RuleRowAi = ({
  rule, idx,
  processVariables, waitReplyNodes, chatBotNodes, chatOrderingCartNodes, eFormNodes,
  aiProviders, aiStaffs, aiStaffOptions, loadingAiStaffs,
  handleAiStaffChange, handleNodeDataChange, selectedNode,
  patchRule, t,
}) => {
  const activeModes = rule.generationModes || ['integrateWaitReply'];

  const toggleMode = (mode, checked) => {
    let next = checked
      ? [...new Set([...activeModes, mode])]
      : activeModes.filter((m) => m !== mode);
    if (next.length === 0) next = ['integrateWaitReply'];
    patchRule(idx, { generationModes: next });
  };

  const aiStaffProps = {
    selectedNode,
    aiStaffs,
    aiStaffOptions,
    loadingAiStaffs,
    handleAiStaffChange: (staffId) => patchRule(idx, { aiStaffId: staffId }),
    handleNodeDataChange,
    t,
  };

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      {/* AI 資料來源多選 */}
      <Form.Item label={t('workflowDesigner.processDataCell.aiSources')} style={{ marginBottom: 4 }}>
        <Space direction="vertical" style={{ width: '100%' }}>
          {AI_SOURCE_OPTIONS.map(({ value, labelKey }) => (
            <Checkbox
              key={value}
              checked={activeModes.includes(value)}
              onChange={(e) => toggleMode(value, e.target.checked)}
            >
              {t(`workflowDesigner.${labelKey}`) || value}
            </Checkbox>
          ))}
        </Space>
      </Form.Item>

      {/* 各來源節點選擇 */}
      {activeModes.includes('integrateWaitReply') && (
        <Form.Item label={t('workflowDesigner.sendEForm.integrateWaitReplyNodeId') || '等待回覆節點'} style={{ marginBottom: 4 }}>
          <Select
            value={rule.integratedWaitReplyNodeId || undefined}
            onChange={(v) => patchRule(idx, { integratedWaitReplyNodeId: v })}
            allowClear
            size="small"
            placeholder={t('workflowDesigner.sendEForm.selectWaitReplyNode') || '自動選取最後一筆'}
          >
            {waitReplyNodes.map((n) => (
              <Option key={n.id} value={n.id}>{n.data?.taskName || n.id}</Option>
            ))}
          </Select>
        </Form.Item>
      )}
      {activeModes.includes('integrateChatBot') && (
        <Form.Item label={t('workflowDesigner.sendEForm.integrateChatBotNodeId') || 'Chatbot 節點'} style={{ marginBottom: 4 }}>
          <Select
            value={rule.integratedChatBotNodeId || undefined}
            onChange={(v) => patchRule(idx, { integratedChatBotNodeId: v })}
            allowClear
            size="small"
            placeholder={t('workflowDesigner.sendEForm.selectChatBotNode') || '自動選取最近 session'}
          >
            {chatBotNodes.map((n) => (
              <Option key={n.id} value={n.id}>{n.data?.taskName || n.id}</Option>
            ))}
          </Select>
        </Form.Item>
      )}
      {activeModes.includes('integrateChatOrderingCart') && (
        <Form.Item label={t('workflowDesigner.sendEForm.selectUpstreamChatOrderingCartNode') || 'Chatbot 購物車節點'} style={{ marginBottom: 4 }}>
          <Select
            value={rule.integratedChatOrderingCartNodeId || undefined}
            onChange={(v) => patchRule(idx, { integratedChatOrderingCartNodeId: v })}
            allowClear
            size="small"
            placeholder={t('workflowDesigner.sendEForm.selectUpstreamChatOrderingCartNodePlaceholder') || '自動選取最近購物車 session'}
          >
            {(chatOrderingCartNodes || []).map((n) => (
              <Option key={n.id} value={n.id}>{n.data?.taskName || n.id}</Option>
            ))}
          </Select>
        </Form.Item>
      )}
      {activeModes.includes('integrateEForm') && (
        <Form.Item label={t('workflowDesigner.sendEForm.integratedEFormNodeId') || 'eForm 節點'} style={{ marginBottom: 4 }}>
          <Select
            value={rule.integratedEFormNodeId || undefined}
            onChange={(v) => patchRule(idx, { integratedEFormNodeId: v })}
            allowClear
            size="small"
          >
            {eFormNodes.map((n) => (
              <Option key={n.id} value={n.id}>{n.data?.taskName || n.id}</Option>
            ))}
          </Select>
        </Form.Item>
      )}

      {/* AI Provider */}
      <Form.Item label={t('workflowDesigner.aiProvider') || 'AI 提供商'} style={{ marginBottom: 4 }}>
        <Select
          value={rule.aiProviderKey || undefined}
          onChange={(v) => patchRule(idx, { aiProviderKey: v })}
          allowClear
          size="small"
          placeholder={t('workflowDesigner.selectAiProvider') || '選擇 AI 提供商'}
        >
          {aiProviders.map((p) => (
            <Option key={p.key || p.providerKey} value={p.key || p.providerKey}>
              {p.name || p.key || p.providerKey}
            </Option>
          ))}
        </Select>
      </Form.Item>

      {/* 輸出流程資料 */}
      <Form.Item label={t('workflowDesigner.processDataCell.aiOutputVariable')} style={{ marginBottom: 4 }}>
        <PvSelect
          value={rule.targetVariable}
          onChange={(v) => patchRule(idx, { targetVariable: v })}
          processVariables={processVariables}
          placeholder={t('workflowDesigner.processDataCell.aiOutputVariablePlaceholder')}
        />
      </Form.Item>

      {/* 若輸出是 JSON 型，可選指定單一欄位 */}
      <Form.Item label={t('workflowDesigner.processDataCell.targetJsonKey')} style={{ marginBottom: 4 }}>
        <Input
          size="small"
          value={rule.outputJsonKey || ''}
          onChange={(e) => patchRule(idx, { outputJsonKey: e.target.value })}
          placeholder={t('workflowDesigner.processDataCell.targetJsonKeyPlaceholder')}
        />
      </Form.Item>

      {/* Prompt */}
      <Form.Item label={t('workflowDesigner.processDataCell.aiAdditionalPrompt')} style={{ marginBottom: 4 }}>
        <Input.TextArea
          rows={3}
          value={rule.additionalPrompt || ''}
          onChange={(e) => patchRule(idx, { additionalPrompt: e.target.value })}
          placeholder={t('workflowDesigner.processDataCell.aiAdditionalPromptPlaceholder')}
        />
      </Form.Item>

      {/* 可用流程資料提示 */}
      {processVariables.length > 0 && (
        <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>
          {t('workflowDesigner.availableVariables') || '可用流程資料'}：
          {processVariables.map((pv) => (
            <Tag key={pv.variableName} style={{ cursor: 'pointer', fontSize: 11, marginBottom: 2 }}
              onClick={() => {
                const cur = rule.additionalPrompt || '';
                patchRule(idx, { additionalPrompt: cur + ' ${' + pv.variableName + '}' });
              }}
            >
              {pv.variableName}
            </Tag>
          ))}
        </div>
      )}
    </Space>
  );
};

export default ProcessDataCellEditor;
